import React from 'react'
import { Mail, PhoneOutgoing } from 'react-feather'
import { useNavigate } from 'react-router-dom'

import { WidgetHeaderWithNavigation } from '@frame/ui-components/widget-header'
import { A } from '@frame/ui-primitives/a'
import { Body } from '@frame/ui-primitives/body'
import { Button } from '@frame/ui-primitives/button'
import { Card } from '@frame/ui-primitives/card'
import { H2 } from '@frame/ui-primitives/h2'
import { H4 } from '@frame/ui-primitives/h4'
import { P } from '@frame/ui-primitives/p'
import { Span } from '@frame/ui-primitives/span'

import { TrueworkJsFeedbackReason as FeedbackReason } from '@frame/lib/api/common/types'
import { InternalEventType } from '@frame/lib/api/events/types'
import { TW_EMAIL, TW_PHONE } from '@frame/lib/constants'
import { useAutoNavigate } from '@frame/lib/hooks/useAutoNavigate'
import { useEventHandler } from '@frame/lib/hooks/useEventHandler'
import { useStore } from '@frame/lib/stores/useStore'
import { getTextForVerificationType } from '@frame/lib/utils/session'
import { routes } from '@frame/routes'
import { DataAttribute } from '@frame/types'

export function EmployerNotFound() {
  const navigate = useNavigate()
  const { autoNavigate } = useAutoNavigate()
  const { reportType } = useStore(({ session }) => ({
    reportType: session?.verification_type,
  }))
  const { handleEvent } = useEventHandler()

  const incomeOrEmployment = getTextForVerificationType(reportType)

  return (
    <>
      <WidgetHeaderWithNavigation cancelButton={{ visible: false }} />
      <Body data-cy={DataAttribute.EMPLOYER_NOT_FOUND_SCREEN} hasBackgroundColor={false} className="p-5">
        <H2>We've got your back!</H2>
        <P variant="small" className="mt-5">
          If you weren't able to find your employer, here are a few options you can try:
        </P>
        <div className="space-y-4">
          <Card intent="primary" className="mt-4 p-5">
            <Span className="mb-2 text-xs font-semibold text-electric-600">Fastest and most accurate</Span>
            <H4 className="mb-2">Log in to employer's payroll account</H4>
            <P variant="small" className="mb-4">
              Ask your employer what payroll system they use and try logging into that payroll directly.
            </P>
            <Button
              data-cy={DataAttribute.EMPLOYER_NOT_FOUND_SEARCH_AGAIN_BUTTON}
              intent="primary"
              onClick={() => {
                autoNavigate()
              }}
            >
              Try again
            </Button>
          </Card>
          <Card intent="secondary" className="mt-4 p-5" border>
            <Span className="mb-2 text-xs font-semibold text-electric-600">Alternative</Span>
            <P variant="small" className="mb-4 mt-2">
              If you are still unable to log in, we'll try to verify your {incomeOrEmployment} information via other
              methods.
            </P>
            <Button
              data-cy={DataAttribute.EMPLOYER_NOT_FOUND_QUIT_BUTTON}
              intent="tertiary"
              onClick={() => {
                handleEvent(InternalEventType.FEEDBACK_SUBMITTED, {
                  partner_event: {
                    feedback: FeedbackReason.UNABLE_TO_FIND_EMPLOYER,
                  },
                })
                navigate(routes.exit, { state: { fromEmployerNotFoundScreen: true } })
              }}
            >
              I can't log in to my payroll
            </Button>
          </Card>
          <Card className="my-4 p-3">
            <div className="flex items-center border-b border-dashed pb-3">
              <P variant="small" className="mr-2 flex-1">
                Learn how to connect your payroll account in 60 seconds
              </P>
              <a href="https://www.truework.com/credentials-applicant-demo-video" target="_blank" rel="noreferrer">
                <Button
                  size="small"
                  intent="tertiary"
                  fullWidth={false}
                  onClick={() => {
                    handleEvent(InternalEventType.CLICKED_VIDEO_LINK)
                  }}
                >
                  Watch video
                </Button>
              </a>
            </div>
            <div className="mt-3">
              <H4 className="mb-3">Still need help?</H4>
              <div className="mb-3 flex items-center">
                <Mail className="mr-3 inline-block text-cobranding" size={16} />
                <A href={`mailto:${TW_EMAIL}`} target="_blank" rel="noreferrer" className="text-sm">
                  {TW_EMAIL}
                </A>
              </div>
              <div className="mb-3 flex items-center">
                <PhoneOutgoing className="mr-3 inline-block text-cobranding" size={16} />
                <A href={`tel:${TW_PHONE}`} target="_blank" rel="noreferrer" className="text-sm">
                  {TW_PHONE}
                </A>
              </div>
            </div>
          </Card>
        </div>
      </Body>
    </>
  )
}
